<script setup lang="ts">
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import { type PropType, computed, reactive, watch } from 'vue'
import { useTranslate } from '@/shared/lib/i18n'

const { trs } = useTranslate()

const props = defineProps({
  field: {
    type: Object as PropType<ObjectInventoryField>,
    required: true,
  },
  hideMandatory: {
    type: Boolean,
    default: false,
  },
})

const emits = defineEmits(['update', 'validation'])

const currentValues = reactive({
  use_min: Boolean(props.field?.use_min),
  use_max: Boolean(props.field?.use_max),
  min: props.field?.min || 0,
  max: props.field?.max || 0,
  precision: props.field?.precision || 0,
  mandatory: Boolean(props.field?.mandatory),
})

/** Текст ошибки, когда обе границы включены, но минимум больше максимума. */
const minMaxError = computed(() => {
  if (!currentValues.use_min || !currentValues.use_max) return ''
  if (Number(currentValues.min) > Number(currentValues.max)) {
    return trs('itf.field_number_min_gt_max')
  }
  return ''
})

const isValid = computed(() => !minMaxError.value)

/** Сообщаем родителю состояние валидности конфигурации границ. */
watch(isValid, (valid) => emits('validation', valid), { immediate: true })

/** Обновляем данные поля */
watch(currentValues, () => {
  emits('update', { ...props.field, ...currentValues })
})
</script>

<template>
  <div class="d-flex align-center mb-2">
    <VCheckbox
      v-model="currentValues.use_min"
      :label="$t('itf.field_number_use_min')"
      density="compact"
      hide-details
    />
    <VTextField
      class="ml-3"
      type="number"
      :label="$t('itf.field_number_min')"
      v-model="currentValues.min"
      :disabled="!currentValues.use_min"
      density="compact"
      hide-details
    />
  </div>

  <div class="d-flex align-center mb-2">
    <VCheckbox
      v-model="currentValues.use_max"
      :label="$t('itf.field_number_use_max')"
      density="compact"
      hide-details
    />
    <VTextField
      class="ml-3"
      type="number"
      :label="$t('itf.field_number_max')"
      v-model="currentValues.max"
      :disabled="!currentValues.use_max"
      density="compact"
      hide-details
    />
  </div>

  <VTextField type="number" :label="$t('itf.field_precision')" v-model="currentValues.precision" />

  <div v-if="minMaxError" class="text-error text-body-2 mt-1 mb-2">{{ minMaxError }}</div>

  <VCheckbox
    v-if="!hideMandatory"
    class="ml-3"
    v-model="currentValues.mandatory"
    :label="$t('itf.required')"
  />
</template>
